import React from 'react';
import axios from 'axios';
import log from '../helpers/Logger';
import APIError from '../helpers/APIError';

// TODO: Check isEmpty usage
log.debug('Building axios instance');
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_GATEWAY_URL ?? '',
});

// Request interceptor
axiosInstance.interceptors.request.use((config) => {
  log.debug(
    `${config.method.toUpperCase()}: ${config.baseURL}${config.url} ${
      config.params ? JSON.stringify(config.params) : ''
    } ${config.data ? JSON.stringify(config.data) : ''}`
  );

  // Build user context header
  const activeAccountRough = localStorage.getItem('activeAccount');
  if (activeAccountRough) {
    const activeAccount = JSON.parse(activeAccountRough);
    const currentUserContext = {};
    if (activeAccount.facilityManager)
      currentUserContext.facilityManagerId = activeAccount.facilityManager.id;
    else if (activeAccount.lhcManager)
      currentUserContext.lhcManagerId = activeAccount.lhcManager.id;
    else if (activeAccount.hcp) currentUserContext.hcpId = activeAccount.hcp.id;
    else currentUserContext.role = activeAccount.role;
    // eslint-disable-next-line no-param-reassign
    config.headers['X-User-Context'] = JSON.stringify(currentUserContext);
  }

  return config;
});

// Response interceptor
axiosInstance.interceptors.response.use(
  (res) => {
    // Log response data
    log.debug('Response:', res.data);
    return res.config.fullResponse ? res : res.data;
  },
  (e) => {
    // TODO: Handle 401
    const statusCode = e.response?.status;
    const errorData = e.response?.data;
    const path = `${e.config?.baseURL}${e.config?.url}`;
    const method = e.config?.method?.toUpperCase();

    if (e.config.logError == null || e.config.logError)
      log.error(`APIError at ${method} ${path}`, null, { statusCode, errorData });

    return Promise.reject(new APIError(e.message, errorData, statusCode));
  }
);

const useAPI = () => {
  /**
   * User login.
   *
   * @param {*} email
   * @param {*} password
   */
  const login = (email, password) =>
    axiosInstance.post('/auth', {
      email,
      password,
    });

  /**
   * Logout API.
   */
  const logout = () => axiosInstance.delete('/auth');

  const forceLogin = (id) =>
    axiosInstance.post(`/auth/force-login/${id}`, {}, { fullResponse: true });

  /**
   * Get user information data.
   *
   */
  const getUser = () => axiosInstance.get('/user/info', { logError: false });

  const createSupervisor = (values) =>
    axiosInstance.post('/user/supervisor', values, { fullResponse: true });

  /**
   * Update user data
   *
   * @param {*} values
   */
  const editOwnUser = (values) => axiosInstance.patch('/user', values);

  const editUser = (id, values) => axiosInstance.patch(`/user/${id}`, values);

  const deleteUser = (id) => axiosInstance.delete(`/user/${id}`);

  const deleteHcp = (id) => axiosInstance.delete(`/hcp/${id}`);

  const getHcp = (id) => axiosInstance.get(`/hcp/${id}`);

  /**
   * Update hcp data
   *
   * @param {*} values
   */
  const editOwnPersonalData = (values) => axiosInstance.patch('/hcp/own', values);

  const editHcp = (id, values) => axiosInstance.patch(`/hcp/${id}`, values);

  const editLhcManager = (values) => axiosInstance.patch('/lhc/manager/own', values);

  const editFacilityManager = (values) => axiosInstance.patch('/facility/manager/own', values);

  const exportHcps = (params = {}) =>
    axiosInstance.get(`/hcp/export`, { params, responseType: 'blob' });

  /**
   * Update workgroup data
   *
   * @param {*} values
   */
  const editWorkgroupOwn = (values) => axiosInstance.patch('/workgroup/own', values);

  const deleteWorkgroup = (id) => axiosInstance.delete(`/workgroup/${id}`);

  const editWorkgroupUserStatus = (id, active) =>
    axiosInstance.patch(`/workgroup/${id}/status`, { active });

  /**
   * Update workgroup
   *
   * @param {*} workgroupId
   * @param {*} data
   * @returns
   */
  const editWorkgroupConfig = (workgroupId, config) =>
    axiosInstance.patch(`/workgroup/${workgroupId}/config`, { config });

  /**
   * Reset password for the email passed in input.
   *
   * @param {*} email
   */
  const forgotPasswordEmail = (email) => axiosInstance.post(`/user/forgot-password`, { email });

  /**
   * Verify resetPassword token
   *
   * @param {*} token
   */
  const resetPasswordTokenVerify = (token) =>
    axiosInstance.get('/user/reset-password', { params: { token } });

  /**
   * reset user password
   *
   * @param {*} token
   */
  const resetPassword = (token, password) =>
    axiosInstance.post('/user/reset-password', { token, password }, { fullResponse: true });

  /**
   * Create invite
   *
   * @param {String} type One of 'lead' or 'member'
   * @param {String} params
   */
  const createInvite = (type, data) =>
    axiosInstance.post(`/invite/${type}`, { ...data }, { fullResponse: true });

  /**
   * Revoke invite
   *
   * @param {*} inviteId
   */
  const revokeInvite = (inviteId) => axiosInstance.patch(`/invite/${inviteId}/withdraw`);

  /**
   * Resend invite
   *
   * @param {*} inviteId
   */
  const resendInvite = (inviteId) => axiosInstance.patch(`/invite/${inviteId}/resend`);

  /**
   * @param {String} type One of 'lead' or 'member'
   */
  const listInvite = (type, filters, paginationData) =>
    axiosInstance.get(`/invite/list/${type}/own`, { params: { ...paginationData, ...filters } });

  /**
   * Get invite data
   *
   * @param {*} inviteCode
   */
  const getInviteData = (inviteCode) => axiosInstance.get(`/invite/code/${inviteCode}`);

  /**
   * Accept invite
   *
   * @param {*} inviteCode
   * @param {*} data
   */
  const acceptInvite = (inviteCode, data) =>
    axiosInstance.patch(`/invite/${inviteCode}/accept`, data, { fullResponse: true });

  /**
   * List Newborn
   *
   * @param {*} filters
   * @param {*} paginationData
   */
  const listNewborn = (filters, paginationData) =>
    axiosInstance.get(`/newborn/list/own`, {
      params: { ...paginationData, ...filters },
    });

  const listNewbornInFacility = (params) =>
    axiosInstance.get('/newborn/list/facility', {
      params,
    });

  /**
   * Search newborn to transfer
   *
   */
  const searchNewbornToTransfer = (filters) =>
    axiosInstance.get(`/newborn/list/transfer`, {
      params: {
        ...filters,
      },
    });

  /**
   * Transfer newborn
   *
   */
  const transferNewborn = (newbornId, filters) =>
    axiosInstance.patch(`/newborn/${newbornId}/transfer`, null, {
      params: {
        ...filters,
      },
    });

  /**
   * Add Newborn
   *
   * @param {*} data
   */
  const addNewborn = (data, params = {}) => axiosInstance.post(`/newborn`, data, { params });

  const deleteNewborn = (id) => axiosInstance.delete(`/newborn/${id}`);

  /**
   * Get Newborn
   *
   * @param {*} newbornId
   */
  const getNewborn = (newbornId) => axiosInstance.get(`/newborn/${newbornId}`);

  /**
   *  Edit Newborn
   *
   * @param {*} newbornId
   * @param {*} data
   */
  const editNewborn = (newbornId, data) => axiosInstance.patch(`/newborn/${newbornId}`, data);

  const getWorkgroupHistory = (newbornId, filters) =>
    axiosInstance.get(`/newborn/${newbornId}/history`, { params: { ...filters } });

  /**
   *  Add Shipping
   *
   * @param {*} data
   */
  const addShipping = (data) => axiosInstance.post('/shipping/', data);

  /**
   * Delete Shipping
   *
   * @param {*} shippingId
   */
  const deleteShipping = (shippingId) => axiosInstance.delete(`/shipping/${shippingId}`);

  /**
   * List Shipping
   *
   * @param {*} data
   * @param {*} status
   */
  const listShipping = (status, filters, paginationData) =>
    axiosInstance.get(`/shipping/list/${status}`, {
      params: { ...paginationData, ...filters },
    });

  /**
   *
   * @param {*} shippingId
   */
  const sendShipping = (shippingId) => axiosInstance.patch(`/shipping/${shippingId}/send`);

  /**
   * List of shipping's samples
   *
   * @param {*} shippingId
   */
  const listSamplesShipped = (shippingId) => axiosInstance.get(`/shipping/${shippingId}/samples`);

  /**
   * Add Sample shipped
   *
   * @param {*} shippingId
   * @param {*} barcode
   */
  const addSampleShipped = (shippingId, barcode) =>
    axiosInstance.post('/shipping/sample', { shippingId, barcode });

  /**
   * Delete sample shipped
   */
  const deleteSampleShipped = (sampleShippedId) =>
    axiosInstance.delete(`/shipping/sample/${sampleShippedId}`);

  /**
   * Get Shipping
   *
   * @param {*} shippingId
   */
  const getShipping = (shippingId) => axiosInstance.get(`/shipping/${shippingId}`);

  /**
   * Download Shipping
   */
  const downloadShipping = (shippingId) =>
    axiosInstance.get(`/shipping/${shippingId}/print`, { responseType: 'blob' });

  /**
   * Get samples of newborn
   *
   * @param {*} newbornId
   */
  const listNewbornSample = (paginationData, newbornId, withAnalyzableSamples = false) =>
    axiosInstance.get(`/sample/list/newborn`, {
      params: { ...paginationData, newbornId, withAnalyzableSamples },
    });

  const listNewbornSampleRequest = (paginationData, newbornId) =>
    axiosInstance.get(`/sample-request/list/newborn`, {
      params: { ...paginationData, newbornId },
    });

  /**
   * Get data for dashboard of metabolic
   */
  const metabolicSummary = () => axiosInstance.get(`/newborn/summary`);

  /**
   * Get sample
   *
   * @param {*} id
   */
  const getSample = (id) => axiosInstance.get(`/sample/${id}`);

  /**
   * Get sample motivation
   */
  const getSampleMotivationsList = () => axiosInstance.get('/sample/motivations');

  /**
   * Get sampling motivation settings
   *
   * @param {String} newbornId
   */
  const getSamplingMotivationSettings = (newbornId) =>
    axiosInstance.get('/sample/motivations-settings', {
      params: {
        newbornId,
      },
    });

  /**
   * Get Italian provinces
   */
  const getProvinces = () =>
    axiosInstance
      .get(`/static/enum/provinces.json`, {
        baseURL: '',
      })
      .then((provinces) =>
        provinces?.reduce((res, province) => {
          res[province?.name] = province?.name;
          return res;
        }, {})
      );

  /**
   * Create a request support
   */
  const requestSupport = (data) => axiosInstance.post(`/system/support`, data);

  /**
   * Get enums
   *
   * @param {String} type The type of the enum (e.g. facility)
   * @param {String} query The string to search for
   * @param {String} key The key to search string in
   * @param {Boolean} resolveId If true, the action is a reverse lookup
   * @param {Object} params
   */
  const getEnum = (type, query, key, resolveId = false, params) =>
    axiosInstance.get(`/enum/${type}`, {
      params: {
        chars: query,
        researchKey: key,
        reverse: resolveId,
        ...params,
      },
    });

  /**
   * Get workgroup
   *
   * @param {*} id
   */
  const getWorkgroup = (id) =>
    axiosInstance.get(`/workgroup/${id}`, {
      params: {
        complete: true,
      },
    });

  const editWorkgroupLead = (workgroupId, data) =>
    axiosInstance.patch(`/workgroup/${workgroupId}/lead`, data);

  const editWorkgroupFull = (workgroupId, data) =>
    axiosInstance.patch(`/workgroup/${workgroupId}`, data);

  const listFacilities = (filters, paginationData) =>
    axiosInstance.get(`/facility/list`, {
      params: { ...paginationData, ...filters },
    });

  const listFacilityManagers = () => axiosInstance.get('/facility/manager/list');

  const getFacility = (facilityId) => axiosInstance.get(`/facility/${facilityId}`);

  const addFacility = (data) => axiosInstance.post(`/facility`, data);

  const editFacility = (facilityId, data) => axiosInstance.patch(`/facility/${facilityId}`, data);

  const listWorkgroups = (filters, paginationData) =>
    axiosInstance.get(`/workgroup/list`, {
      params: { ...paginationData, ...filters },
    });

  const listHcps = (filters, paginationData) =>
    axiosInstance.get(`/hcp/list`, {
      params: { ...paginationData, complete: true, ...filters },
    });

  const facilitiesPerformanceStatistics = (filters) =>
    axiosInstance.get(`/facility/performance-statistics`, {
      params: { ...filters },
    });

  const facilitiesMetabolicPerformanceStatistics = (filters) =>
    axiosInstance.get(`/facility/performance-statistics/metabolic`, {
      params: { ...filters },
    });

  const facilitiesMetabolicPerformanceStatisticsExport = (params) =>
    axiosInstance.get(`/facility/performance-statistics/metabolic/export`, {
      params,
      responseType: 'blob',
      fullResponse: true,
    });

  const facilitiesPerformanceStatisticsExport = (filters) =>
    axiosInstance.get(`/facility/performance-statistics/export`, {
      params: { ...filters },
      responseType: 'blob',
    });

  const facilitiesBirthDataStatistics = (filters) =>
    axiosInstance.get(`/facility/birth-data-statistics`, {
      params: { ...filters },
    });

  const facilitiesBirthDataStatisticsExport = (filters) =>
    axiosInstance.get(`/facility/birth-data-statistics/export`, {
      params: { ...filters },
      responseType: 'blob',
    });

  const listSupervisors = (filters, paginationData) =>
    axiosInstance.get(`/user/list/supervisor`, {
      params: { ...paginationData, ...filters },
    });

  const listLhcs = () => axiosInstance.get('/lhc/list');

  const listLhcManagers = () => axiosInstance.get('/lhc/manager/list');

  const addLhcManager = (data) => axiosInstance.post('/lhc/manager', data);

  const addFacilityManager = (data) => axiosInstance.post('/facility/manager', data);

  /** *
   * Get reports
   *
   */
  const listReport = (filters, paginationData) =>
    axiosInstance.get(`/report/list`, {
      params: { ...paginationData, ...filters },
    });

  const confirmReport = (reportId) => axiosInstance.patch(`/report/${reportId}/confirm`);

  const refuseReport = (reportId) => axiosInstance.patch(`/report/${reportId}/refuse-confirmation`);

  /**
   * Get samples
   *
   * @param {String} status One of 'invalid', 'valid' or 'ready'
   */
  const listSample = (status, paginationData) => {
    let path;
    let params;

    if (status === 'ready') {
      path = '/sample/list/to-send';
      params = { ...paginationData };
    } else {
      path = '/sample/list/own';
      params = { ...paginationData, analyzableSampleStatus: status };
    }

    return axiosInstance.get(path, {
      params,
    });
  };

  const getSampleAnalysis = (sampleId) => axiosInstance.get(`/sample/${sampleId}/sample-analysis`);

  /**
   * Get report by id
   *
   * @param {String} reportId
   */

  const downloadReport = (reportId) =>
    axiosInstance.get(`/report/${reportId}/pdf`, { responseType: 'blob' });

  /**
   * Get summary info of a newborn
   *
   * @param {String} newbornId
   */
  const getNewbornSummary = (newbornId) => axiosInstance.get(`/newborn/summary/${newbornId}`);

  /**
   * Get summary report of a newborn
   * @param {*} newbornId
   * @returns
   */
  const getNewbornResultSummary = (newbornId) =>
    axiosInstance.get(`/newborn/summary-result/${newbornId}`);

  /**
   * List of protocol
   *
   * @param {*} filters
   * @param {*} paginationData
   */
  const listProtocol = (filters, paginationData) =>
    axiosInstance.get(`/newborn/list/protocol`, { params: { ...filters, ...paginationData } });

  const cedap = {
    getEnum: (name, chars, keys = undefined, hideDeleted = false, filters = {}) =>
      axiosInstance.get(`/cedap/enum/${name}`, {
        params: { chars, keys, hideDeleted, ...filters },
      }),

    getCedapList: (status, filteredValues = {}) =>
      axiosInstance.get(`/cedap/list/status`, {
        params: {
          status,
          ...filteredValues,
        },
      }),

    getCedap: (cedapId) => axiosInstance.get(`/cedap/${cedapId}`),

    getSectionA: (cedapId) => axiosInstance.get(`/section-a/${cedapId}`),

    getSectionB: (cedapId) => axiosInstance.get(`/section-b/${cedapId}`),

    getChild: (cedapId, sdo, params = {}) =>
      axiosInstance.get(`/child/${cedapId}/${sdo}`, { params, logError: false }),

    getChildById: (childId, params = {}) => axiosInstance.get(`/child/${childId}`, { params }),

    getChildByNewbornId: (newbornId, params = {}) =>
      axiosInstance.get(`/child/newborn/${newbornId}`, { params }),

    updateChildById: (childId, child) => axiosInstance.patch(`/child/${childId}`, child),

    updateChild: (cedapId, sdo, child) => axiosInstance.patch(`/child/${cedapId}/${sdo}`, child),

    rectifyRegistryChild: (childId) => axiosInstance.patch(`/child/${childId}/rectify-registry`),

    createChild: (cedapId, child, childId = null) =>
      axiosInstance.post(`/child/${cedapId}`, { child, childId }),

    cedapInitialize: (values) => axiosInstance.post('/cedap/initialize', values),

    updateCedapBasicData: (cedapId, values) =>
      axiosInstance.patch(`/cedap/basic-data/${cedapId}`, values),

    updateSectionA: (cedapId, sectionA) => axiosInstance.patch(`/section-a/${cedapId}`, sectionA),

    updateSectionB: (cedapId, sectionB) => axiosInstance.patch(`/section-b/${cedapId}`, sectionB),

    setObstetricHistory: (cedapId, sectionB) =>
      axiosInstance.patch(`/section-b/${cedapId}/obstetric-history`, sectionB),

    setPregnancyData: (cedapId, sectionB) =>
      axiosInstance.patch(`/section-b/${cedapId}/pregnancy`, sectionB),

    setChildbirthData: (cedapId, sectionB) =>
      axiosInstance.patch(`/section-b/${cedapId}/childbirth`, sectionB),

    updatePreviousConceptionsData: (cedapId, sectionB) =>
      axiosInstance.patch(`/section-b/${cedapId}/previous-conceptions`, sectionB),

    updateCedapStatus: (cedapId, sdo, status, params = {}) => {
      const url = sdo
        ? `/child/${cedapId}/${sdo}/status/${status}`
        : `/cedap/${cedapId}/status/${status}`;
      return axiosInstance.patch(url, params);
    },

    updateChildStatus: (cedapId, sdo, status, params = {}) =>
      axiosInstance.patch(`/child/${cedapId}/${sdo}/status/${status}`, params),

    updateChildReferentValidation: (childId, referentValidation, section) =>
      axiosInstance.patch(`/child/${childId}/referent-validation/${section}`, {
        referentValidation,
      }),

    getCedapCountData: (status = [], filtersValues = {}, what = 'cedap') =>
      axiosInstance.get(`/${what}/count/status`, {
        params: {
          status,
          ...filtersValues,
        },
      }),

    getSummary: () => axiosInstance.get(`/cedap/summary`),

    getCountNewbornsWoCedap: () => axiosInstance.get('/child/count/no-cedap'),

    getCedapViewData: (currentPage, itemsPerPage, status = [], what = 'cedap') =>
      axiosInstance.get(`/${what}/list/status`, {
        params: {
          status,
          currentPage,
          itemsPerPage,
        },
      }),

    getCedapSectionDEViewData: (searchFilters, paginationData) =>
      axiosInstance.get(`/child/list/referent-validation`, {
        params: {
          ...searchFilters,
          ...paginationData,
        },
      }),

    search: (what, params, currentPage, itemsPerPage) =>
      axiosInstance.get(`/${what}/list/advanced-search`, {
        params: {
          ...params,
          currentPage,
          itemsPerPage,
        },
      }),

    getCedapComplete: (id) => axiosInstance.get(`/cedap/${id}/complete`),

    editCedapComplete: (id, values) => axiosInstance.patch(`/cedap/${id}/complete`, values),

    deleteCedap: (id) => axiosInstance.delete(`/cedap/${id}`),

    getStatistics: (params) =>
      axiosInstance.get(`/cedap-metrics/childbirths`, {
        params: {
          ...params,
        },
      }),

    getSmaStatistics: (params) =>
      axiosInstance.get(`/cedap-metrics/childbirths/sma`, {
        params,
      }),

    exportSMAStatistics: (params) =>
      axiosInstance.get('/cedap-metrics/childbirths/sma/export', {
        responseType: 'blob',
        params,
        fullResponse: true,
      }),

    exportStatistics: (params) =>
      axiosInstance.get('/cedap-metrics/childbirths/export', {
        responseType: 'blob',
        params,
        fullResponse: true,
      }),

    getBreastfeedingStatistics: (params) =>
      axiosInstance.get('/cedap-metrics/breastfeeding', { params }),

    exportBreastfeedingStatistics: (params) =>
      axiosInstance.get('/cedap-metrics/breastfeeding/export', {
        responseType: 'blob',
        params,
        fullResponse: true,
      }),

    getRppStatistics: (params) => axiosInstance.get('/cedap-metrics/rpp', { params }),

    exportRppStatistics: (params) =>
      axiosInstance.get('/cedap-metrics/rpp/export', {
        responseType: 'blob',
        params,
        fullResponse: true,
      }),

    exportStatisticsCedap: (params) =>
      axiosInstance.get('/cedap-metrics/cedap/export', {
        responseType: 'blob',
        params,
        fullResponse: true,
      }),

    getFilteredData: (currentPage, itemsPerPage, filtersValues, status, what = 'cedap') =>
      axiosInstance.get(`/${what}/list/status`, {
        params: {
          status,
          currentPage,
          itemsPerPage,
          ...filtersValues,
        },
      }),

    downloadCedap: (id, sdo) => {
      const url = sdo ? `/child/${id}/${sdo}/export` : `/cedap/${id}/export`;
      return axiosInstance.get(url, {
        responseType: 'blob',
      });
    },

    deleteChildSection: (id, sdo, section) =>
      axiosInstance.delete(`/child/${id}/${sdo}/${section}`),

    getPersonalData: (identifier) => axiosInstance.get(`/sinfonia/patients/${identifier}`),

    searchMother: (motherData) =>
      axiosInstance.post(`/sinfonia/patients/search`, {
        ...motherData,
      }),

    checkFiscalCode: (fiscalCode) => axiosInstance.get(`/cedap/fc/${fiscalCode}`),

    downloadCob: (id, index) =>
      axiosInstance.get(`/cob/${id}/export`, {
        responseType: 'blob',
        params: {
          index: index || 0,
        },
      }),

    downloadAllCobs: (id) =>
      axiosInstance.get(`/cob/${id}/export-all`, {
        responseType: 'blob',
      }),

    generateCob: (id, index, values) => {
      const params = index ? { index } : {};

      return axiosInstance.patch(`/cob/${id}`, values, { params });
    },

    cobsList: (currentPage, itemsPerPage, params) =>
      axiosInstance.get(`/cob/list`, {
        params: {
          ...params,
          currentPage,
          itemsPerPage,
        },
      }),

    getNewsList: (currentPage, itemsPerPage) =>
      axiosInstance.get(`/cedap-news/list`, {
        params: {
          currentPage,
          itemsPerPage,
        },
      }),

    createNews: (news) => axiosInstance.post('/cedap-news', news),

    getNews: (newsId) => axiosInstance.get(`/cedap-news/${newsId}`),

    updateNews: (newsId, news) => axiosInstance.patch(`/cedap-news/${newsId}`, news),

    deleteNews: (newsId) => axiosInstance.delete(`/cedap-news/${newsId}`),
  };

  const auditive = {
    /**
     * Get the hearing screening of a newborn
     *
     * @param {String} newbornId
     */
    getHS: (newbornId) => axiosInstance.get(`/hs/${newbornId}`, { logError: false }),

    getFullHS: (newbornId) => axiosInstance.get(`/hs/${newbornId}/full`),

    /**
     * Add hearing screening to a newborn
     *
     * @param {String} newbornId
     */
    addHS: (newbornId, data) =>
      axiosInstance.post('/hs', {
        newbornId,
        ...data,
      }),

    /**
     * Edit hearing screening
     *
     * @param {String} newbornId
     */
    editHS: (newbornId, data) => axiosInstance.patch(`/hs/${newbornId}`, data),

    editFullHS: (newbornId, data) => axiosInstance.patch(`/hs/${newbornId}/full`, data),

    deleteHS: (newbornId) => axiosInstance.delete(`/hs/${newbornId}`),

    listHS: (params) => axiosInstance.get('/hs/list', { params }),

    getVisit: (visitId) => axiosInstance.get(`/visit/${visitId}`),

    /**
     * List first level visit
     *
     * @param {String} newbornId
     */
    listFirstLevelVisits: (newbornId) =>
      axiosInstance.get(`/visit/list/newborn/${newbornId}/first-level`),

    listSecondLevelVisits: (newbornId) => axiosInstance.get(`/visit/list/newborn/${newbornId}`),

    addOtoemissionFirstLevel: (visitId, data) =>
      axiosInstance.post(`/otoemission/first-level`, {
        visitId,
        ...data,
      }),

    editOtoemission: (id, data) => axiosInstance.patch(`/otoemission/${id}`, data),

    getOtoemission: (id) => axiosInstance.get(`/otoemission/${id}`),

    addOtoemissionSecondLevel: (visitId, data) =>
      axiosInstance.post(`/otoemission`, {
        visitId,
        ...data,
      }),

    addOtoscopic: (visitId, data) =>
      axiosInstance.post(`/otoscopic`, {
        visitId,
        ...data,
      }),

    editOtoscopic: (id, data) => axiosInstance.patch(`/otoscopic/${id}`, data),

    getOtoscopic: (id) => axiosInstance.get(`/otoscopic/${id}`),

    signalToSecondLevel: (newbornId, data) =>
      axiosInstance.post(`/hs/second-level-signaling`, {
        newbornId,
        ...data,
      }),

    signalToThirdLevel: (newbornId, data) =>
      axiosInstance.post(`/hs/third-level-signaling`, {
        newbornId,
        ...data,
      }),

    exportAllHS: (params) =>
      axiosInstance.get('/hs/export-all', {
        responseType: 'blob',
        fullResponse: true,
        params,
      }),

    downloadHSReminder: (newbornId) =>
      axiosInstance.get(`/hs/${newbornId}/export-reminder/first-level`, { responseType: 'blob' }),

    downloadHSReminderSecondLevel: (newbornId) =>
      axiosInstance.get(`/hs/${newbornId}/export-reminder/second-level`, { responseType: 'blob' }),

    addEvokedPotentialsFirstLevel: (visitId, data) =>
      axiosInstance.post(`/evoked-potential/first-level`, {
        visitId,
        ...data,
      }),

    editEvokedPotentials: (id, data) => axiosInstance.patch(`/evoked-potential/${id}`, data),

    getEvokedPotentials: (id) => axiosInstance.get(`/evoked-potential/${id}`),

    addEvokedPotentialsSecondLevel: (visitId, data) =>
      axiosInstance.post(`/evoked-potential`, {
        visitId,
        ...data,
      }),

    addImpedentiometrySecondLevel: (visitId, data) =>
      axiosInstance.post(`/impedentiometry`, {
        visitId,
        ...data,
      }),

    editImpedentiometry: (id, data) => axiosInstance.patch(`/impedentiometry/${id}`, data),

    getImpedentiometry: (id) => axiosInstance.get(`/impedentiometry/${id}`),

    /** Get hearing screening counter */
    getHSLv1CountSummary: () => axiosInstance.get('/hs/summary/first-level'),

    getHSLv2CountSummary: () => axiosInstance.get('/hs/summary/second-level'),

    listNewbornWoHS: (paginationData) =>
      axiosInstance.get('/newborn/list/own', {
        params: { ...paginationData, noHs: true },
      }),

    listFirstLevelNewborn: (filters, paginationData) =>
      axiosInstance.get('/hs/first-level/list', {
        params: {
          ...paginationData,
          ...(filters ?? {}),
        },
      }),

    /** *
     * Get new born info second level HS
     *
     */
    getNewbornSecondLevelSummary: (newbornId) => axiosInstance.get(`/hs/${newbornId}/summary/lv2`),

    /** *
     * Edit newborn second level HS ( only fiscal code and tutor info)
     *
     */
    editNewbornInfo: (newbornId, data) => axiosInstance.patch(`/hs/${newbornId}/tutor`, data),

    /** *
     * Edit newborn familiarities second level HS
     *
     */
    editNewbornLv2: (newbornId, data) => axiosInstance.patch(`/hs/${newbornId}/second-level`, data),
    /* Method to edit HS booking
     *
     * @param {*} id
     * @param {*} data
     *
     * TODO: chiedere conferma di rotta
     */
    editHSBooking: (id, data) => axiosInstance.patch(`/hs/booking/${id}`, data),

    /** *
     * Method to set newborn out of screening
     *
     * @param {*} id
     * @param {*} data
     *
     * TODO: chiedere conferma di rotta
     */
    exitHS: (id, data) => axiosInstance.patch(`/hs/exit/${id}`, data),

    addDiagnosis: (data) => axiosInstance.post(`/hs/diagnosis`, data),

    editDiagnosis: (id, data) => axiosInstance.patch(`/hs/${id}/diagnosis`, data),

    getDiagnosis: (id) => axiosInstance.get(`/hs/${id}/diagnosis/`),

    /**
     * Function called to get montly booking
     *
     */
    getMonthlyBooking: (month, year) => {
      const queryParams = {};
      if (month) queryParams.month = month;
      if (year) queryParams.year = year;
      return axiosInstance.get('/visit/calendar', { params: queryParams });
    },

    /**
     * Function called to get daily booking
     *
     */
    listDailyBooking: (minDate, maxDate) =>
      axiosInstance.get('/visit/list', {
        params: { bookingDateMin: minDate, bookingDateMax: maxDate },
      }),

    /**
     * Function called to get pending booking
     *
     */
    listPendingBooking: () =>
      axiosInstance.get('/visit/list', {
        params: {
          bookingDateNull: true,
        },
      }),

    /**
     * It gets the list of lost reservations
     */
    lostReservations: () =>
      axiosInstance.get('/visit/list', {
        params: {
          lostReservations: true,
        },
      }),

    /**
     * Function called to get pending booking
     *
     */
    listArchivedBooking: (searchFilters, paginationData) =>
      axiosInstance.get('/visit/list', {
        params: {
          resultNotNull: true,
          ...searchFilters,
          ...paginationData,
        },
      }),

    visitList: (filters, paginationData) =>
      axiosInstance.get('/visit/list', {
        params: {
          ...paginationData,
          ...filters,
        },
      }),

    /**
     * Function called to set tests not executable
     *
     */
    notExecutable: (data) => axiosInstance.patch('/visit/not-executable', data),

    /**
     * Function called to change booking date
     *
     */
    editBooking: (id, data) => axiosInstance.patch(`/visit/${id}`, data),

    archiveVisit: (id) => axiosInstance.patch(`/visit/${id}/archive`),

    /**
     * Function called to add booking date
     *
     */
    addBooking: (data) => axiosInstance.post(`/visit`, data),

    /**
     * Set visit result
     *
     */
    setVisitResult: (id, data) => axiosInstance.patch(`/visit/${id}/result`, data),

    firstLevelStatistics: (filters) =>
      axiosInstance.get(`/hs/first-level-statistics/`, {
        params: { ...filters },
      }),

    firstLevelStatisticsExport: (filters) =>
      axiosInstance.get(`/hs/first-level-statistics/export`, {
        params: { ...filters },
        responseType: 'blob',
      }),

    secondLevelStatistics: (filters) =>
      axiosInstance.get(`/hs/second-level-statistics/`, {
        params: { ...filters },
      }),

    secondLevelStatisticsExport: (filters) =>
      axiosInstance.get(`/hs/second-level-statistics/export`, {
        params: { ...filters },
        responseType: 'blob',
      }),

    /**
     * Export Surveillance
     *
     */
    exportSurveillance: () =>
      axiosInstance.get(`/hs/export-surveillance`, { responseType: 'blob' }),

    listHSSecondLevel: (filters, paginationData) =>
      axiosInstance.get('/hs/second-level/list', {
        params: {
          ...paginationData,
          ...filters,
        },
      }),

    listHSThirdLevel: (filters, paginationData) =>
      axiosInstance.get('/hs/third-level/list', {
        params: {
          ...paginationData,
          ...filters,
        },
      }),

    addVisit: (newbornId, bookingDate) =>
      axiosInstance.post(`/visit`, {
        newbornId,
        bookingDate,
      }),

    editVisit: (visitId, data) => axiosInstance.patch(`/visit/${visitId}`, data),

    getSecondLevelWorkgroupList: (params) =>
      axiosInstance.get('/workgroup/auditive/list', { params }),

    getStatistics: (filters = {}) =>
      axiosInstance.get('/hs/statistics', {
        params: filters,
      }),

    exportStatistics: () =>
      axiosInstance.get('/hs/statistics/export', {
        responseType: 'blob',
        fullResponse: true,
      }),

    exportNewbornFirstLevel: (params) =>
      axiosInstance.get('/hs/newborn/export', {
        responseType: 'blob',
        params,
      }),

    /**
     * Agenda
     */
    listMonthlyAvailability: (month, year) => {
      const queryParams = {};
      if (month) queryParams.month = month;
      if (year) queryParams.year = year;
      return axiosInstance.get('/availability/calendar', { params: queryParams });
    },

    listDailyAvailability: (start, end) =>
      axiosInstance.get('/availability/daily', {
        params: { start, end },
      }),

    createAvailability: (data) => axiosInstance.post('/availability/', data),

    editAvailability: (data) => axiosInstance.patch('/availability/redefine', data),

    deleteAvailability: (data) => axiosInstance.post('/availability/delete-intervals', data),

    annualAvailability: (workgroupId, newbornId) =>
      axiosInstance.get('/availability/annual', {
        params: { workgroupId, newbornId },
      }),

    getHoursAvailability: (start, end, id) =>
      axiosInstance.get('/availability/hours-available', {
        params: { start, end, workgroupId: id },
      }),

    getStatisticsOfStatistics: (filters) =>
      axiosInstance.get('hs/risk-factor-statistics', {
        params: filters,
      }),
  };

  const ophthalmology = {
    /**
     * Get the hearing screening of a newborn
     *
     * @param {String} newbornId
     */
    getOFS: (newbornId) => axiosInstance.get(`/os/${newbornId}`, { logError: false }),

    getFullOFS: (newbornId) => axiosInstance.get(`/os/${newbornId}/full`),

    /**
     * Add hearing screening to a newborn
     *
     * @param {String} newbornId
     */
    addOFS: (newbornId, data) =>
      axiosInstance.post('/os', {
        newbornId,
        ...data,
      }),

    /**
     * Edit hearing screening
     *
     * @param {String} newbornId
     */
    editOFS: (newbornId, data) => axiosInstance.patch(`/os/${newbornId}`, data),

    editFullOFS: (newbornId, data) => axiosInstance.patch(`/os/${newbornId}/full`, data),

    deleteOFS: (newbornId) => axiosInstance.delete(`/os/${newbornId}`),

    listOFS: (params) => axiosInstance.get('/os/list', { params }),

    getVisit: (visitId) => axiosInstance.get(`os/visit/${visitId}`),

    setVisitResult: (id, data) => axiosInstance.patch(`/os/visit/${id}/result`, data),

    listFirstLevelVisits: (newbornId) =>
      axiosInstance.get(`/os/visit/list`, {
        params: { newbornId },
      }),

    addRedReflex: (visitId, data) =>
      axiosInstance.post(`/redreflex`, {
        visitId,
        ...data,
      }),

    editRedReflex: (id, data) => axiosInstance.patch(`/redreflex/${id}`, data),

    getRedReflex: (id) => axiosInstance.get(`/redreflex/${id}`),

    // second level otoscopy
    addOphthamolscopySecondLevel: (visitId, data) =>
      axiosInstance.post(`/ophthamolscopy`, {
        visitId,
        ...data,
      }),

    editOophthamolscopy: (id, data) => axiosInstance.patch(`/ophthamolscopy/${id}`, data),

    getOphthamolscopy: (id) => axiosInstance.get(`/ophthamolscopy/${id}`),

    listSecondLevelVisits: (newbornId) => axiosInstance.get(`/os/visit/list/newborn/${newbornId}`),

    nextLevelSignaling: (newbornId, data) =>
      axiosInstance.post(`/os/next-level-signaling`, {
        newbornId,
        ...data,
      }),

    signalToThirdLevel: (newbornId, data) =>
      axiosInstance.post(`/os/third-level-signaling`, {
        newbornId,
        ...data,
      }),

    exportAllOFS: (params) =>
      axiosInstance.get('/os/export-all', {
        responseType: 'blob',
        fullResponse: true,
        params,
      }),

    downloadOFSReminder: (newbornId) =>
      axiosInstance.get(`/os/${newbornId}/export-reminder/lev1ToLev2`, { responseType: 'blob' }),

    downloadOFSReminderSecondLevel: (newbornId) =>
      axiosInstance.get(`/os/${newbornId}/export-reminder/lev2ToLev3`, { responseType: 'blob' }),

    /** Get hearing screening counter */
    getOFSLv1CountSummary: () => axiosInstance.get('/os/summary/first-level'),

    getOFSLv2CountSummary: () => axiosInstance.get('/os/summary/second-level'),

    listNewbornWoOFS: (paginationData) =>
      axiosInstance.get('/newborn/list/own', {
        params: { ...paginationData, noOs: true },
      }),

    listFirstLevelNewborn: (filters, paginationData) =>
      axiosInstance.get('/os/first-level/list', {
        params: {
          ...paginationData,
          ...(filters ?? {}),
        },
      }),

    /** *
     * Get new born info second level HS
     *
     */
    getNewbornSecondLevelSummary: (newbornId) => axiosInstance.get(`/os/${newbornId}/summary/lv2`),

    /** *
     * Edit newborn second level os ( only fiscal code and tutor info)
     *
     */
    editNewbornInfo: (newbornId, data) => axiosInstance.patch(`/os/${newbornId}/tutor`, data),

    /** *
     * Edit newborn familiarities second level os
     *
     */
    editNewbornLv2: (newbornId, data) => axiosInstance.patch(`/os/${newbornId}`, data),
    /* Method to edit HS booking
     *
     * @param {*} id
     * @param {*} data
     *
     * TODO: chiedere conferma di rotta
     */
    editOFSBooking: (id, data) => axiosInstance.patch(`/os/booking/${id}`, data),

    /** *
     * Method to set newborn out of screening
     *
     * @param {*} id
     * @param {*} data
     *
     * TODO: chiedere conferma di rotta
     */
    exitOFS: (id, data) => axiosInstance.patch(`/os/exit/${id}`, data),

    addDiagnosis: (data) => axiosInstance.post(`/os/diagnosis`, data),

    editDiagnosis: (id, data) => axiosInstance.patch(`/os/${id}/diagnosis`, data),

    getDiagnosis: (id) => axiosInstance.get(`/os/${id}/diagnosis/`),

    /**
     * Function called to get montly booking
     *
     */
    getMonthlyBooking: (month, year, workgroupId) => {
      const queryParams = {};
      if (month) queryParams.month = month;
      if (year) queryParams.year = year;
      if (workgroupId) queryParams.id = +workgroupId;
      return axiosInstance.get('/os/visit/calendar', { params: queryParams });
    },

    /**
     * Function called to get daily booking
     *
     */
    listDailyBooking: (minDate, maxDate, workgroupLevel) =>
      axiosInstance.get('/os/visit/list', {
        params: { bookingDateMin: minDate, bookingDateMax: maxDate, workgroupLevel },
      }),

    /**
     * Function called to get pending booking
     *
     */
    listPendingBooking: (params) =>
      axiosInstance.get('/os/visit/list', {
        params: {
          bookingDateNull: true,
          ...params,
        },
      }),

    /**
     * It gets the list of lost reservations
     */
    lostReservations: () =>
      axiosInstance.get('/os/visit/list', {
        params: {
          lostReservations: true,
        },
      }),

    /**
     * Function called to get pending booking
     *
     */
    listArchivedBooking: (searchFilters, paginationData) =>
      axiosInstance.get('/os/visit/list', {
        params: {
          resultNotNull: true,
          ...searchFilters,
          ...paginationData,
        },
      }),

    visitList: (filters, paginationData) =>
      axiosInstance.get('/os/visit/list', {
        params: {
          ...paginationData,
          ...filters,
        },
      }),

    /**
     * Function called to set tests not executable
     *
     */
    notExecutable: (data) => axiosInstance.patch('/os/visit/not-executable', data),

    /**
     * Function called to change booking date
     *
     */
    editBooking: (id, data) => axiosInstance.patch(`/os/visit/${id}`, data),

    archiveVisit: (id) => axiosInstance.patch(`/os/visit/${id}/archive`),

    /**
     * Function called to add booking date
     *
     */
    addBooking: (data) => axiosInstance.post(`/os/visit`, data),

    firstLevelStatistics: (filters) =>
      axiosInstance.get(`os/first-level-statistics/`, {
        params: { ...filters },
      }),

    firstLevelStatisticsExport: (filters) =>
      axiosInstance.get(`os/first-level-statistics/export`, {
        params: { ...filters },
        responseType: 'blob',
      }),

    secondLevelStatistics: (filters) =>
      axiosInstance.get(`os/second-level-statistics/`, {
        params: { ...filters },
      }),

    secondLevelStatisticsExport: (filters) =>
      axiosInstance.get(`os/second-level-statistics/export`, {
        params: { ...filters },
        responseType: 'blob',
      }),

    /**
     * Export Surveillance
     *
     */
    exportSurveillance: () => axiosInstance.get(`os/export-surveillance`, { responseType: 'blob' }),

    lisOFSSecondLevel: (filters, paginationData) =>
      axiosInstance.get('/os/second-level/list', {
        params: {
          ...paginationData,
          ...filters,
        },
      }),

    listOFSThirdLevel: (filters, paginationData) =>
      axiosInstance.get('/os/third-level/list', {
        params: {
          ...paginationData,
          ...filters,
        },
      }),

    addVisit: (newbornId, bookingDate) =>
      axiosInstance.post(`/os/visit`, {
        newbornId,
        bookingDate,
      }),

    editVisit: (visitId, data) => axiosInstance.patch(`/os/visit/${visitId}`, data),

    getSecondLevelWorkgroupList: (params) =>
      axiosInstance.get('/workgroup/ophthalmological/list', { params }),

    getStatistics: (filters = {}) =>
      axiosInstance.get('/os/statistics', {
        params: filters,
      }),

    exportStatistics: () =>
      axiosInstance.get('/os/statistics/export', {
        responseType: 'blob',
        fullResponse: true,
      }),

    exportNewbornFirstLevel: (params) =>
      axiosInstance.get('/os/newborn/export', {
        responseType: 'blob',
        params,
      }),

    /**
     * Agenda
     */
    listMonthlyAvailability: (month, year) => {
      const queryParams = {};
      if (month) queryParams.month = month;
      if (year) queryParams.year = year;
      return axiosInstance.get('/os/availability/calendar', { params: queryParams });
    },

    listDailyAvailability: (start, end) =>
      axiosInstance
        .get('/os/availability/list', {
          params: { start, end },
        })
        .then((res) => {
          return { intervals: res.data };
        }),

    createAvailability: (data) => axiosInstance.post('/os/availability/', data),

    editAvailability: (data) => axiosInstance.patch('/os/availability/redefine', data),

    deleteAvailability: (data) => axiosInstance.post('/os/availability/delete-intervals', data),

    annualAvailability: (workgroupId) =>
      axiosInstance.get('os/availability/annual', {
        params: { workgroupId },
      }),

    getHoursAvailability: (start, end, id) =>
      axiosInstance.get('os/availability/hours-available', {
        params: { start, end, workgroupId: id },
      }),
  };

  /**
   * Archive notification
   *
   * @param {Array} ids
   */
  const setNotificationWorked = (data) =>
    axiosInstance.patch(`/notification/set-worked-multiple`, data);

  /**
   * Get notification
   *
   * @param {String} notificationId
   *
   */
  const getNotification = (notificationId) => axiosInstance.get(`/notification/${notificationId}`);

  /**
   * Print notification
   *
   * @param {String} notificationId
   *
   */
  const downloadNotification = (notificationId) =>
    axiosInstance.get(`/notification/${notificationId}/print`, { responseType: 'blob' });
  /**
   * List notification
   *
   */
  const listNotifications = (filters, paginationData) =>
    axiosInstance.get('/notification/list', {
      params: { ...filters, ...paginationData },
    });

  /**
   * Get notification count
   *
   */
  const getNotificationCount = (params) =>
    axiosInstance.get('/notification/count', {
      params,
    });

  /**
   * Add Sample
   *
   * @param {*} data
   */
  const addSample = (data) => axiosInstance.post('/sample', data);

  /**
   *  Edit Sample
   *
   * @param {*} sampleId
   * @param {*} data
   */
  const editSample = (sampleId, data) => axiosInstance.patch(`/sample/${sampleId}`, data);

  /**
   * Mortality Registry
   */
  const listMortalityRegistry = (filters, paginationData) =>
    axiosInstance.get('/mortality-registry/list', {
      params: { ...paginationData, ...filters },
    });

  const addMortalityRegistry = (data) => axiosInstance.post('/mortality-registry', data);

  const editMortalityRegistry = (id, data) =>
    axiosInstance.patch(`/mortality-registry/${id}`, data);

  const deleteMortalityRegistry = (id) => axiosInstance.delete(`/mortality-registry/${id}`);

  const exportMortalityRegistry = (params) =>
    axiosInstance.get('/mortality-registry/export', { params, responseType: 'blob' });

  const updateLastActivity = () => axiosInstance.patch('/auth/last-activity');

  /**
   * Pediatric Centre
   */
  const takeCharge = (followUpId, data) => axiosInstance.patch(`/follow-up/${followUpId}`, data);

  const takeChargeCreate = (data) => axiosInstance.post('/follow-up/pediatric', data);

  const followUpList = (filters, paginationData) =>
    axiosInstance.get('/follow-up/list', {
      params: { ...paginationData, ...filters },
    });

  const sampleRequestsList = (filters, paginationData) =>
    axiosInstance.get('/sample-request/list/own', {
      params: { ...paginationData, ...filters },
    });

  const downloadSampleRequestFile = (id) =>
    axiosInstance.get(`/sample-request/${id}/download-report`, {
      responseType: 'blob',
      fullResponse: true,
    });

  const downloadAnalytes = (sampleId) =>
    axiosInstance.get(`/sample/${sampleId}/analytes/export`, {
      responseType: 'blob',
      fullResponse: true,
    });

  const serumRequestList = (filters, paginationData) =>
    axiosInstance.get('/sample-request/list/own/serum', {
      params: { ...paginationData, ...filters },
    });

  const mergeAccounts = (data) => axiosInstance.post('/user/merge-accounts', data);

  const exitFromScreening = (data) => axiosInstance.post('/exit-screening', data);

  const getOngoingScreenings = (newbornId) =>
    axiosInstance.get(`/screening/list/ongoing`, {
      params: { newbornIds: [`${newbornId}`] },
    });

  const listExitedScreenings = (newbornId, pagination) =>
    axiosInstance.get('/exit-screening/list', { params: { newbornId, ...pagination } });

  const editShippingBarcode = (shippingId, values) =>
    axiosInstance.patch(`/shipping/${shippingId}`, values);

  return React.useMemo(
    () => ({
      login,
      forceLogin,
      logout,
      getUser,
      getHcp,
      createSupervisor,
      editOwnUser,
      editUser,
      deleteUser,
      editOwnPersonalData,
      editHcp,
      editLhcManager,
      editFacilityManager,
      exportHcps,
      editWorkgroupConfig,
      editWorkgroupOwn,
      deleteWorkgroup,
      editWorkgroupUserStatus,
      forgotPasswordEmail,
      resetPasswordTokenVerify,
      resetPassword,
      createInvite,
      listInvite,
      getInviteData,
      revokeInvite,
      resendInvite,
      acceptInvite,
      listNewborn,
      addNewborn,
      deleteNewborn,
      getNewborn,
      editNewborn,
      getWorkgroupHistory,
      getProvinces,
      getEnum,
      facilitiesPerformanceStatistics,
      facilitiesMetabolicPerformanceStatistics,
      facilitiesMetabolicPerformanceStatisticsExport,
      facilitiesPerformanceStatisticsExport,
      facilitiesBirthDataStatistics,
      facilitiesBirthDataStatisticsExport,
      requestSupport,
      addShipping,
      deleteShipping,
      listShipping,
      getShipping,
      sendShipping,
      listSamplesShipped,
      downloadShipping,
      addSampleShipped,
      deleteSampleShipped,
      searchNewbornToTransfer,
      transferNewborn,
      listFacilities,
      listFacilityManagers,
      getFacility,
      addFacility,
      editFacility,
      listWorkgroups,
      listHcps,
      listSupervisors,
      listLhcs,
      listLhcManagers,
      addLhcManager,
      addFacilityManager,
      listReport,
      confirmReport,
      refuseReport,
      getSample,
      getSampleMotivationsList,
      getSamplingMotivationSettings,
      getWorkgroup,
      editWorkgroupLead,
      editWorkgroupFull,
      listSample,
      downloadReport,
      listNewbornSample,
      listNewbornSampleRequest,
      getNewbornSummary,
      getNewbornResultSummary,
      metabolicSummary,
      cedap,
      auditive,
      listProtocol,
      getNotification,
      downloadNotification,
      setNotificationWorked,
      listNotifications,
      getNotificationCount,
      addSample,
      editSample,
      listMortalityRegistry,
      addMortalityRegistry,
      exportMortalityRegistry,
      updateLastActivity,
      takeCharge,
      followUpList,
      getSampleAnalysis,
      sampleRequestsList,
      takeChargeCreate,
      serumRequestList,
      downloadSampleRequestFile,
      downloadAnalytes,
      listNewbornInFacility,
      editMortalityRegistry,
      deleteMortalityRegistry,
      mergeAccounts,
      deleteHcp,
      ophthalmology,
      exitFromScreening,
      getOngoingScreenings,
      listExitedScreenings,
      editShippingBarcode,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
};

export default useAPI;
